import { RouterProvider } from 'react-router-dom'
import RootRouter from './routes/root.route'
import {
  persist,
  RootState,
  store,
  useAppDispatch,
  useAppSelector,
} from './utils/agent.store'
import { USER_PROFILES } from './utils/constants'
import {
  LoginTypeEnum,
  setAuthProfile,
  clearForceMsalPromptNextLogin,
} from '@app-store'
import { useEffect } from 'react'
import { performAgentDeskLogout } from './session/agentLogout'
import SessionIdleCountdownProvider from './session/SessionInactivityManager'
import MandatoryReloginManager from './session/MandatoryReloginManager'
import { invalidateToken } from './utils/helperFunctions'
import {
  subscribeCrossTabLogout,
  subscribeCrossTabLoginSuccess,
} from '@ecrm-session'
import { allowMsalShellForCrossTabLoginFollowAfterLogout } from '@ecrm-msal'
import {
  clearSessionPostLogoutReturnPath,
  getSessionPostLogoutReturnPath,
  isSafeReturnPath,
} from './utils/postLogoutReturnPath'

const RouterWrapper = () => {
  const dispatch = useAppDispatch()
  const userSuperAdmin = useAppSelector(
    (state: RootState) => state.authUser.user?.superAdmin
  )
  const selectedTenantId = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.id
  )

  useEffect(() => {
    return subscribeCrossTabLogout((loginType) => {
      const t = store.getState().session.token
      void performAgentDeskLogout({
        dispatch: store.dispatch,
        persist,
        token: t,
        loginType: loginType ?? LoginTypeEnum.ADMIN_FORCED_LOGOUT,
        broadcastCrossTab: false,
        invalidateToken: invalidateToken,
      })
    })
  }, [])

  useEffect(() => {
    return subscribeCrossTabLoginSuccess(() => {
      if (store.getState().authUser.isAuthenticated !== false) return
      if (window.location.pathname !== '/logout') return

      allowMsalShellForCrossTabLoginFollowAfterLogout()
      const path = getSessionPostLogoutReturnPath()
      clearSessionPostLogoutReturnPath()
      store.dispatch(clearForceMsalPromptNextLogin())
      const origin = window.location.origin
      const target =
        path && isSafeReturnPath(path) ? `${origin}${path}` : origin
      void persist.flush().then(() => {
        window.location.href = target
      })
    })
  }, [])

  useEffect(() => {
    if (window.location.href.includes('/error')) return
    const authState = store.getState().authUser
    const userData = authState.user
    if (!userData) return
    if (userData?.clients?.length === 0)
      window.location.href = window.location.origin + `/error/user-not-found`
    if (userData?.profile === USER_PROFILES.ACCESS_MANAGER) {
      window.location.href = window.location.origin + `/error/user-not-found`
      return
    }
    if (userData?.profile === USER_PROFILES.SUPPORT) {
      const filteredClients =
        userData?.clients?.filter(
          (client) => client?.agent_visibility === true
        ) || []
      dispatch(
        setAuthProfile({
          ...authState,
          user: {
            ...userData,
            superAdmin: false,
            clients: filteredClients,
          },
        })
      )
      if (filteredClients.length === 0) {
        window.location.href = window.location.origin + `/error/user-not-found`
      }
    }
  }, [userSuperAdmin, dispatch])

  useEffect(() => {
    if (!selectedTenantId) return
    if (!('serviceWorker' in navigator)) return

    const sendMessage = () => {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SET_ACTIVE_CLIENT',
          clientId: String(selectedTenantId),
        })
      }
    }

    if (navigator.serviceWorker.controller) {
      sendMessage()
    } else {
      navigator.serviceWorker.ready.then(sendMessage)
    }
  }, [selectedTenantId])

  return (
    <SessionIdleCountdownProvider>
      <RouterProvider router={RootRouter} />
      <MandatoryReloginManager />
    </SessionIdleCountdownProvider>
  )
}

export default RouterWrapper
